import { STONE_HARBOR } from "./stoneHarbor";
import type { AlertLocation } from "./types";

export const STONE_HARBOR_ALERT_RADIUS_MILES = 10;
export const ALERT_LOCATION_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const EARTH_RADIUS_MILES = 3958.7613;

export function distanceMilesFromStoneHarbor(latitude: number, longitude: number): number {
  return distanceMiles(STONE_HARBOR.latitude, STONE_HARBOR.longitude, latitude, longitude);
}

export function isInStoneHarborAlertArea(latitude: number, longitude: number): boolean {
  return distanceMilesFromStoneHarbor(latitude, longitude) <= STONE_HARBOR_ALERT_RADIUS_MILES;
}

export function alertLocationFromCoordinates(latitude: number, longitude: number, checkedAt: Date = new Date()): AlertLocation {
  const distanceMilesValue = distanceMilesFromStoneHarbor(latitude, longitude);
  const inArea = distanceMilesValue <= STONE_HARBOR_ALERT_RADIUS_MILES;
  return {
    status: inArea ? "inside" : "outside",
    inArea,
    area: STONE_HARBOR.name,
    checkedAt: checkedAt.toISOString(),
    radiusMiles: STONE_HARBOR_ALERT_RADIUS_MILES,
    distanceMiles: Math.round(distanceMilesValue * 10) / 10,
    reason: inArea
      ? "This phone recently confirmed it is within 10 miles of Stone Harbor."
      : "This phone is outside the Stone Harbor alert area, so push alerts are paused."
  };
}

export function unavailableAlertLocation(reason: string, checkedAt: Date = new Date()): AlertLocation {
  return {
    status: "unavailable",
    inArea: false,
    area: STONE_HARBOR.name,
    checkedAt: checkedAt.toISOString(),
    radiusMiles: STONE_HARBOR_ALERT_RADIUS_MILES,
    reason
  };
}

export function isAlertLocationEligible(location?: AlertLocation, now: Date = new Date()): boolean {
  if (!location?.inArea || location.status !== "inside") return false;
  const checkedAt = Date.parse(location.checkedAt);
  if (Number.isNaN(checkedAt)) return false;
  return now.getTime() - checkedAt <= ALERT_LOCATION_MAX_AGE_MS;
}

function distanceMiles(latA: number, lonA: number, latB: number, lonB: number): number {
  if (![latA, lonA, latB, lonB].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const phiA = toRadians(latA);
  const phiB = toRadians(latB);
  const deltaPhi = toRadians(latB - latA);
  const deltaLambda = toRadians(lonB - lonA);
  const haversine = Math.sin(deltaPhi / 2) ** 2 + Math.cos(phiA) * Math.cos(phiB) * Math.sin(deltaLambda / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function toRadians(degrees: number): number {
  return degrees * Math.PI / 180;
}
